import { AnyAction } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { MyrAPIService } from '../../../api/myr/myrAPI';
import { IReduxRootState } from '../../../app/reduxRootState.interface';
import {
    ErrorResolution,
    ErrorResolutionTypes,
} from '../../../app/common/errorResolution/errorResolution';
import { ErrorResolutionActions } from '../../../app/common/errorResolution/errorResolutionActions';
import { NotificationsActions } from '../../../app/common/notifications/notificationsActions';
import { TranslationHelpers } from '../../../i18n/translationHelpers';
import AccountSecurityActions from '../accountSecurityActions';
import { UpdatePasswordAction, UpdatePasswordActionType } from './updatePasswordActionType';
import { IUpdatePasswordRequest } from './updatePasswordRequest';
import { MyrAPIEndpoints } from '../../../api/myr/myrAPIEndpoints.constant';
import ErrorRedirectService from '../../../routes/error/errorRedirect.service';
import { NOTIFICATIONS } from '../../../app/common/constants/styles';
import { NotificationType } from '../../../app/common/notifications/notificationType.enum';
import { RevokeSessionActionTypes } from '../revokeSession/revokeSessionActionTypes';
import { addRatEvent } from '../../../utils/ratClickEvents';

export class UpdatePasswordActions {
    public static updateMemberPassword(
        data: IUpdatePasswordRequest,
    ): ThunkAction<void, IReduxRootState, {}, AnyAction> {
        return async (
            dispatch: ThunkDispatch<IReduxRootState, {}, AnyAction>,
            getState: () => IReduxRootState,
        ) => {
            try {
                dispatch(request());
                await MyrAPIService.sendRequest(MyrAPIEndpoints.PASSWORD, 'POST', data);
                const tokenExpirationUrl = getState().app.clientConfig.tokenExpirationUrl;
                return onPasswordUpdateAndRevokeSuccess(dispatch, tokenExpirationUrl);
            } catch (error) {
                const errorResolution = error as ErrorResolution;
                if (errorResolution.type === ErrorResolutionTypes.PARTIAL_SUCCESS) {
                    return onPasswordUpdateSuccessWithRevokeFailure(dispatch);
                }
                return failure(dispatch, errorResolution);
            }
        };

        function request(): UpdatePasswordAction {
            return { type: UpdatePasswordActionType.UPDATE_PASSWORD_REQUEST, payload: {} };
        }

        function onPasswordUpdateAndRevokeSuccess(
            dispatch: ThunkDispatch<IReduxRootState, {}, AnyAction>,
            tokenExpirationUrl: string,
        ) {
            addRatEvent('password_update', 'password_edit_success');
            addRatEvent('session_deactivation', 'revoke_user_sessions_success');
            dispatch({ type: UpdatePasswordActionType.UPDATE_PASSWORD_SUCCESS, payload: {} });
            dispatch({
                type: RevokeSessionActionTypes.SEND_SESSION_REVOKE_SUCCESS,
            });
            // show success notification for password update & session revocation
            dispatch(
                NotificationsActions.showSuccess(
                    TranslationHelpers.getFormattedMessage(
                        'notifications.account.password.update.success',
                    ),
                ),
            );
            dispatch(
                NotificationsActions.showRedirectSuccess('notification.revoke.session.success'),
            );
            dispatch(AccountSecurityActions.closeAccountSecurityOverlay());
            // redirect the user to login page after session revocation
            setInterval(() => {
                ErrorRedirectService.takeToLogout(tokenExpirationUrl.toString());
            }, NOTIFICATIONS[NotificationType.Success_Redirecting].expiry);
        }

        function onPasswordUpdateSuccessWithRevokeFailure(
            dispatch: ThunkDispatch<IReduxRootState, {}, AnyAction>,
        ) {
            addRatEvent('password_update', 'password_edit_success');
            dispatch({ type: UpdatePasswordActionType.UPDATE_PASSWORD_SUCCESS, payload: {} });
            // show success notification for password update & failed toast for session revocation
            dispatch(
                NotificationsActions.showSuccess(
                    TranslationHelpers.getFormattedMessage(
                        'notifications.account.password.update.success',
                    ),
                ),
            );
            dispatch(
                NotificationsActions.showError(
                    TranslationHelpers.getFormattedMessage(
                        'notifications.account.password.update.session_revocation_failed',
                        {
                            linkText: TranslationHelpers.getFormattedMessage(
                                'revoke.session.button.text',
                            ),
                            pageName: TranslationHelpers.getFormattedMessage('profile.account'),
                        },
                    ),
                ),
            );
            dispatch(AccountSecurityActions.closeAccountSecurityOverlay());
        }

        function failure(
            dispatch: ThunkDispatch<IReduxRootState, {}, AnyAction>,
            errorResolution: ErrorResolution,
        ) {
            dispatch({
                payload: { errorResolution },
                type: UpdatePasswordActionType.UPDATE_PASSWORD_FAILURE,
            });
            dispatch(
                ErrorResolutionActions.processErrorResolution(
                    errorResolution,
                    'notifications.account.password.update.failure',
                    { challengerPid: data.challengeData.cpid },
                ),
            );
        }
    }
}
